import type { FeedItem } from "@compass/shared/schema/api/feed";
import { Radio, Video } from "lucide-react";
import { useUserTimezone } from "hooks/useUserTimezone";

type Props = Extract<FeedItem, { type: "upcomingWebinar" }>;

export const FeedWebinarCard = ({ webinar }: Props) => {
    const timeZone = useUserTimezone();
    const startTime = new Date(webinar.startTime);
    const now = new Date();
    const endTime = webinar.endTime ? new Date(webinar.endTime) : null;
    const isLive = startTime <= now && (!endTime || endTime > now);
    const dateStr = startTime.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone,
    });
    const timeStr = startTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    });

    return (
        <div className="overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm transition-shadow hover:shadow-md">
            {webinar.thumbnail && (
                <div className="relative h-36 w-full overflow-hidden">
                    <img
                        src={webinar.thumbnail}
                        alt={webinar.title}
                        className="h-full w-full object-cover"
                    />
                    {isLive && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
                            <Radio className="h-3 w-3 animate-pulse" />
                            LIVE NOW
                        </div>
                    )}
                </div>
            )}
            <div className="p-4">
                <div className="mb-2 flex items-center gap-1.5">
                    <Video className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-medium text-blue-600">
                        {isLive ? "Live Webinar" : "Upcoming Webinar"}
                    </span>
                </div>
                <h3 className="line-clamp-2 font-semibold text-gray-900">{webinar.title}</h3>
                {webinar.description && (
                    <p className="line-clamp-2 mt-1 text-sm text-gray-600">{webinar.description}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                    {dateStr} at {timeStr}
                </p>
            </div>
        </div>
    );
};
